import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  Spin,
  Switch, // Add Switch import
} from "antd";
import { useTranslation } from "react-i18next";
import {
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "antd/dist/antd.min.css";
import "../../styles/Main.scss";
import ButtonField from "../../common/htmlComponents/ButtonField";
import { ErrorNotificationMsg, SuccessNotificationMsg } from "../../utils/Notifications";
import { getRequest, postRequest } from "../../axios";
import { authUserData } from "../../utils/Helper";
import { ReactComponent as Archiveicon } from "../../image/Archiveicon.svg";
import SearchIcon from "../../image/SearchIcon.svg";
import PlusSquareOutlined from "../../image/PlusSquareOutlined.svg";

const VendorListSuperAdmin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { Search } = Input;
  const [selectedRow, setSelectedRow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendor, setVendor] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [reload, setReload] = useState(false);
  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
  });

  useEffect(() => {
    const getVendorList = async () => {
      setLoading(true);
      try {
        const fields = [
          "id",
          "vendor_trade_name",
          "person_name",
          "vendor_registered_name",
          "gst_no",
          "registered_business_address_building",
          "registered_business_address_area",
          "registered_business_address_landmark",
          "registered_business_address_pincode_number",
          "registered_business_address_city",
          "email",
          "is_active",
          "is_new",
        ].join(",");
        const queryParams = new URLSearchParams({
          fields: fields,
          page: state.page,
          page_size: state.pagesize,
          ordering: state.ordering,
          ...(state.search && { search: state.search }),
        });
        let apiResponse = await getRequest(`superuser-vendor/?${queryParams.toString()}`, state);
        if (apiResponse.status) {
          setVendor(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_vendor_onboarding_list"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getVendorList();
  }, [state, reload, t]);

  // Table columns
  const columns = [
    {
      title: "Vendor",
      dataIndex: "vendor_trade_name",
      key: "vendor_trade_name",
      sorter: true,
      width: 180,
    },
    {
      title: "Name",
      dataIndex: "person_name",
      key: "person_name",
      sorter: true,
      width: 180,
    },
    {
      title: "Legal Name of Business",
      dataIndex: "vendor_registered_name",
      key: "vendor_registered_name",
      sorter: true,
      width: 200,
    },
    {
      title: "GSTN",
      dataIndex: "gst_no",
      key: "gst_no",
      sorter: true,
      width: 150,
    },
    {
      title: "Registered Business Address",
      dataIndex: "trading_address_building",
      key: "trading_address_building",
      sorter: true,
      width: 200,
      render: (text, record) => {
        const address = [
          record.registered_business_address_building,
          record.registered_business_address_area,
          record.registered_business_address_landmark,
          record.registered_business_address_pincode_number
        ].filter(Boolean).join(" ");
        const displayAddress = address || "N/A";
        
        return (
          <Tooltip title={displayAddress} placement="left">
            <div style={{ 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              maxWidth: '200px'
            }}>
              {displayAddress}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "City",
      dataIndex: "registered_business_address_city",
      key: "registered_business_address_city",
      sorter: true,
      width: 150,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      width: 120,
      fixed: 'right',
      render: (text, record) => (
        <Switch
          checked={record.is_active === true}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          loading={record._statusLoading}
          onChange={checked => handleToggleStatus(record, checked)}
          disabled={record.is_new}
        />
      ),
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (text, record) => (
        <Space>
          <Link to={`/vendor-superadmin/edit/${record.id}`}>
            <EditOutlined />
          </Link>
          <Popconfirm
            className="action"
            title={t("pop_title")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            placement="left"
            onConfirm={() => handleArchive(record.id)}
          >
            <DeleteOutlined />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Table row selection
  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, record.id]
        : selectedRow.filter((key) => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, ...changeRows.map((row) => row.id)]
        : selectedRow.filter((key) => !changeRows.find((row) => row.id === key));
      setSelectedRow(updatedSelectedRowKeys);
    },
    selectedRowKeys: selectedRow,
  };

  // Handle table change (pagination, sorting)
  const handleTableChange = (pagination, filters, sorter) => {
    let orderBy = "id";
    if (sorter && sorter.columnKey && sorter.order) {
      orderBy = sorter.order === "ascend" ? sorter.columnKey : `-${sorter.columnKey}`;
    }
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
    setSelectedRow(selectedRow);
  };

  // Debounced search
  let debounceTimeout = null;
  const onSearch = (value) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      if (value) {
        setState({
          ...state,
          search: value,
        });
      } else {
        const { search, ...newState } = state;
        setState(newState);
      }
    }, 500);
  };

  // Bulk archive
  const handleArchive = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0 && id) {
      ids = [id];
    }
    try {
      let apiResponse = await postRequest("superuser-vendor/archive/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        let totalArchive = totalCount - ids.length;
        if (totalArchive < 11) {
          setState((prevState) => ({
            ...prevState,
            page: 1,
            search: "",
          }));
          setReload(!reload);
        } else {
          setReload(!reload);
        }
        SuccessNotificationMsg(apiResponse.data.message);
        setSelectedRow([]);
      } else {
        ErrorNotificationMsg(Array.isArray(apiResponse.data.message) ? apiResponse.data.message.join(", ") : apiResponse.data.message);
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        page: 1,
        search: "",
      }));
    }
  };

  // Add status toggle handler
  const handleToggleStatus = async (record, checked) => {
    // Set loading for this row
    setVendor(prev => prev.map(v => v.id === record.id ? { ...v, _statusLoading: true } : v));
    try {
      const apiResponse = await postRequest(
        `superuser-vendor/toggle_active/?vendor_id=${record.id}`,
        { action: checked }
      );
      if (apiResponse.data && apiResponse.data.success) {
        SuccessNotificationMsg(apiResponse.data.message || "Status updated");
        setReload(r => !r); // reload list
      } else {
        ErrorNotificationMsg(apiResponse.data.message || "Failed to update status");
        setVendor(prev => prev.map(v => v.id === record.id ? { ...v, _statusLoading: false } : v));
      }
    } catch (error) {
      ErrorNotificationMsg("Failed to update status");
      setVendor(prev => prev.map(v => v.id === record.id ? { ...v, _statusLoading: false } : v));
    }
  };

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip placement="right" title={<span>Manage all vendors onboarded in the system.</span>}>
              <h3>Vendor Onboarding</h3>
            </Tooltip>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Vendor Onboarding</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={<img src={SearchIcon} alt="SearchIcon" className="SearchIcon" width={22} />}
                  placeholder={t("pl_search_list")}
                  onChange={e => onSearch(e.target.value)}
                  style={{ width: 250 }}
                />
              </Space>
            </div>
            <Tooltip placement="right" title={<span>Add a new vendor to the system.</span>}>
              <Button
                className="BlueShadowBtn"
                type="primary"
                onClick={() => navigate("/vendor-superadmin/add")}
                icon={<img src={PlusSquareOutlined} alt="Plus Square" />}
              >
                Add Vendor
              </Button>
            </Tooltip>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleArchive()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label={t("pl_archive")}
                />
              </Popconfirm>
            )}
            <Tooltip placement="topLeft" title="Archived Records">
              <Link to="/vendor-superadmin/archive">
                <Archiveicon />
              </Link>
            </Tooltip>
          </Space>
        </div>
      </div>
      <section className="grid-sec tablegridsec">
        <div className="PR-body">
           <div className="tablewrp">
          <Table
            // scroll={{ x: 1200 }}
            rowSelection={rowSelection}
            columns={columns}
            rowKey={record => record.id}
            dataSource={vendor}
            loading={loading}
            pagination={{
              current: state.page,
              pageSize: state.pagesize,
              total: totalCount,
              showTotal: total => `Total ${total} items`,
              showSizeChanger: true,
              locale: { items_per_page: "Items / Page" },
            }}
            onChange={handleTableChange}
          />
        </div></div>
      </section>
    </>
  );
};

export default VendorListSuperAdmin;
